
import { Options, Vue } from 'vue-class-component';
import AppointmentService from "@/services/AppointmentService";
import { RecordIdentifier } from "@/data/global";
import {
    // AppointmentEnrollment as AppointmentEnrollmentDTO,
    AppointmentEnrollmentIntentResult
} from "@/data/appointments";
import { EnrollmentCriteriaError } from "@/data/enrollments";
import LoginForm from "@/components/LoginForm.vue";
import Alert from "@/components/Alert.vue";
import EventBusService from "@/services/EventBusService";
import CourseOrderable from "@/components/CourseOrderable.vue";
//import AuthService from "@/services/AuthService";
//import UrlService from "@/services/UrlService";

import ProductOrderPaymentModal from "@/components/modals/ProductOrderPaymentModal.vue";
import { parseEnrollmentErrorResult } from "@/utils/enrollment";
import CourseEnrollment from "@/components/CourseEnrollment.vue";
import LoginModal from "@/components/modals/LoginModal.vue";
import NavigationService from "@/services/NavigationService";

@Options({
    props: {
        appointmentId: {
            type: [Number, String],
            required: true,
        },
        regionId: {
            type: [Number, String],
            required: true,
        },
    },
    components: {
        LoginModal,
        CourseEnrollment,
        CourseOrderable,
        Alert,
        LoginForm,
        ProductOrderPaymentModal,
    },
    emits: [
        'enrollment',
        'withdrawal',
        'login',
    ],
})
export default class AppointmentEnrollment extends Vue {
    appointmentId!: RecordIdentifier
    regionId!: RecordIdentifier

    enrollmentRequested = false

    loginModalShown = false
    loginOpen = false

    error = false

    loading = true
    loaded = false

    loadingEnroll = false
    loadingWithdraw = false
    loadingJoinWaitlist = false
    loadingLeaveWaitlist = false

    result: AppointmentEnrollmentIntentResult|null = null

    get isAuthenticated() {
        return this.result?.authenticated;
    }

    get isEnrolledInAppointment() {
        // console.warn(JSON.stringify(this.result?.enrollmentStatus, null, 2));
        return !!this.result?.enrollmentStatus.appointment;
    }
    get isWaitingInAppointment() {
        return !!this.result?.enrollmentStatus.appointmentWaiting;
    }
    get isEnrolledInCourse() {
        return !!this.result?.enrollmentStatus.course;
    }

    get hasToBuyCourse() {
        // course has UserHasCreditForCourse
        return this.hasOnlyErrorKey('UserHasCreditForCourse', 'course');
    }

    get canEnroll() {
        // if we can not enroll appointment because we are not enrolled in course, but the latter is the only error => server will auto-enroll us in course
        if( this.hasOnlyErrorKey('UserIsEnrolledInAppointmentCourse') ) {
            return this.result?.enrollmentIntents?.course?.canEnroll;
        }

        if( !this.isEnrolledInCourse && !this.result?.enrollmentIntents?.course?.canEnroll ) {
            return false;
        }

        if( !this.result?.enrollmentIntents?.appointment?.canEnroll ) {
            return false;
        }

        return true;
    }
    get canJoinWaitlist() {
        return !!this.result?.enrollmentIntents?.appointment?.canJoinWaitlist;
    }

    getHighestPriorityErrorsOfTag(tag?: string): EnrollmentCriteriaError[] {
        return [...this.result?.enrollmentIntents?.appointment?.errors || [], ...this.result?.enrollmentIntents?.course?.errors || []]
            .filter(error => !tag || error.tags.includes(tag))
            .reduce<EnrollmentCriteriaError[]>((list, error) => {
                if( list.length < 1 ) {
                    return [error];
                }
                const max = list[0].priority; // list will only contain entries of the same priority at all times, so use first entry
                if( error.priority > max ) {
                    return [error]; // new max priority found
                }
                if( error.priority === max ) {
                    return [...list, error]; // same max priority, so append
                }
                return list;
            }, []) || [];
    }

    get highestPriorityErrors(): EnrollmentCriteriaError[] {
        return this.getHighestPriorityErrorsOfTag();
    }

    get errorMissingCourseEnrollment() {
        return this.highestPriorityErrors.some(error => error.key === 'UserIsEnrolledInAppointmentCourse');
    }

    get errorMissingAppointmentCredit() {
        return this.highestPriorityErrors.some(error => error.key === 'UserHasCreditForAppointment');
    }

    get errorCourseParticipantsLimitReached() {
        return this.highestPriorityErrors.some(error => error.key === 'CourseParticipantsLimitNotReached');
    }

    get errorAppointmentParticipantsLimitReached() {
        return this.highestPriorityErrors.some(error => error.key === 'AppointmentParticipantsLimitNotReached');
    }

    onEnrollmentRequest() {
        this.loadEnrollmentIntent();
        this.enrollmentRequested = true;
    }


    get appointmentNotEnrollable() {
        return this.result?.enrollmentIntents?.appointment?.hasError.appointment || false;
    }

    hasErrorKey(errorKey: string) {
        return this.result?.enrollmentIntents?.appointment?.errors.some(error => error.key === errorKey) || false;
    }

    hasOnlyErrorKey(errorKey: string, entity: 'appointment' | 'course' = 'appointment') {
        if( !this.result?.enrollmentIntents?.[entity]?.errors || this.result?.enrollmentIntents?.[entity]?.errors?.length === 0 ) {
            return false;
        }

        return this.result?.enrollmentIntents?.[entity]?.errors.every(error => error.key === errorKey) || false;
    }

    /*get isEnrolled() {
        return this.enrollment !== null;
    }*/

    onBuyCredit() {
        EventBusService.$emit('modals.region.products.open', { region: this.regionId });
    }

    onPerformEnrollment() {
        this.loadingEnroll = true;
        return AppointmentService.enrollAppointment(this.appointmentId).then(result => {
            // this.loggedIn = true;

            EventBusService.$notification({
                type: 'success',
                message: 'Deine Anmeldung zum Kurstermin war erfolgreich.',
            })

            this.$emit('enrollment');
            EventBusService.$emit('user.appointments.enrollment:changed', {
                appointment: this.appointmentId,
                action: 'enrolled',
            });
            //this.result = result.data?.data || null;

            //this.error = false;
            //this.loaded = true;
        }).catch(error => {
            const errorMessage = parseEnrollmentErrorResult(error.response.data?.data?.enrollmentIntents);
            EventBusService.$notification({
                type: 'error',
                message: 'Bei deiner Kursterminanmeldung ist ein Fehler aufgetreten' + (errorMessage ? (': ' + errorMessage) : '.'),
            });
            console.log(error.response.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingEnroll = false);
    }

    onPerformWithdrawal() {
        this.loadingWithdraw = true;
        return AppointmentService.withdrawAppointment(this.appointmentId).then(result => {
            // this.loggedIn = true;

            EventBusService.$notification({
                type: 'success',
                message: 'Deine Abmeldung vom Kurstermin war erfolgreich.',
            })

            this.$emit('withdrawal');
            EventBusService.$emit('user.appointments.enrollment:changed', {
                appointment: this.appointmentId,
                action: 'withdrawn',
            });
            //this.result = result.data?.data || null;

            //this.error = false;
            //this.loaded = true;
        }).catch(error => {
            EventBusService.$notification({
                type: 'error',
                message: error.response.data?.error_temp || 'Bei deiner Kursterminabmeldung ist ein Fehler aufgetreten.',
            });
            console.log(error.response.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingWithdraw = false);
    }

    onPerformJoinWaitlist() {
        this.loadingJoinWaitlist = true;
        return AppointmentService.joinAppointmentWaitlist(this.appointmentId).then(result => {
            // this.loggedIn = true;

            EventBusService.$notification({
                type: 'success',
                message: 'Du bist der Warteliste des Kurstermins erfolgreich beigetreten.',
            });

            this.$emit('enrollment');
            EventBusService.$emit('user.appointments.enrollment:changed', {
                appointment: this.appointmentId,
                action: 'waitlistJoined',
            });
            //this.result = result.data?.data || null;

            //this.error = false;
            //this.loaded = true;
        }).catch(error => {
            const errorMessage = parseEnrollmentErrorResult(error.response.data?.data?.enrollmentIntents);
            EventBusService.$notification({
                type: 'error',
                message: 'Beim Beitreten zur Warteliste des Kurstermins ist ein Fehler aufgetreten' + (errorMessage ? (': ' + errorMessage) : '.'),
            });
            console.log(error.response.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingJoinWaitlist = false);
    }

    onPerformLeaveWaitlist() {
        this.loadingLeaveWaitlist = true;
        return AppointmentService.leaveAppointmentWaitlist(this.appointmentId).then(result => {
            // this.loggedIn = true;

            EventBusService.$notification({
                type: 'success',
                message: 'Du hast die Warteliste des Kurstermins erfolgreich verlassen.',
            })

            this.$emit('withdrawal');
            EventBusService.$emit('user.appointments.enrollment:changed', {
                appointment: this.appointmentId,
                action: 'waitlistLeft',
            });
            //this.result = result.data?.data || null;

            //this.error = false;
            //this.loaded = true;
        }).catch(error => {
            EventBusService.$notification({
                type: 'error',
                message: error.response.data?.error_temp || 'Beim Verlassen der Warteliste des Kurstermins ist ein Fehler aufgetreten.',
            });
            console.log(error.response.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingLeaveWaitlist = false);
    }

    loadEnrollmentIntent() {
        this.loading = true;
        return AppointmentService.getAppointmentEnrollmentIntent(this.appointmentId).then(result => {
            // this.loggedIn = true;

            this.result = result.data?.data || null;

            this.error = false;
            this.loaded = true;
        }).catch(error => {
            this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            this.error = true;
        }).finally(() => this.loading = false);
    }

    onCourseOrderSuccess() {
        // console.log('refresh enrollment status');
        this.loadEnrollmentIntent().then(() => {
            // console.log('perform enrollment');
            this.onPerformEnrollment();
        });
    }

    onLogin() {
        this.loadEnrollmentIntent();
        this.$emit('login');
    }

    get courseIsEnrollableDuringRegistration() {
        return this.result?.enrollmentIntents?.course?.creditRequired === false;
    }
    get registerWithCoursePrefilledUrl() {
        return NavigationService.buildRouteUrl('register', { course: this.result?.course?.id + '', appointment: this.appointmentId });
    }
    get registerWithRegionPrefilledUrl() {
        return NavigationService.buildRouteUrl('register', { region: this.regionId });
    }

    openRegionContactForm(): void {
        EventBusService.$emit('modals.region.contact.open', { region: this.regionId });
    }

    mounted() {
        // this.loadEnrollmentIntent();
        this.onEnrollmentRequest();
    }
}
